import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, ShieldCheck, ShieldAlert } from "lucide-react";
import { GovMarkdown, downloadFile, STATUS_STYLES, fmtDate } from "@/components/governance/govUtils";

const NEXT = { draft: ["in_review", "withdrawn"], in_review: ["draft", "approved", "withdrawn"], approved: ["published", "withdrawn"], published: ["withdrawn"] };
const LABEL = { in_review: "Send for review", draft: "Back to draft", approved: "Approve", published: "Publish", withdrawn: "Withdraw" };

export const DocumentDialog = ({ docId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [note, setNote] = useState("");
  const [legalOk, setLegalOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (id = docId) => api.get(`/governance/documents/${id}`).then(({ data }) => { setD(data); setEdit(null); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (docId) load(); }, [docId]);

  const call = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(msg); setD(data); setEdit(null); setNote(""); setLegalOk(false); onChanged(); return data; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const locked = d && ["approved", "published", "superseded", "withdrawn"].includes(d.status);

  return (
    <Dialog open={!!docId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gov-doc-dialog">
        {d && <>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">{d.title}
              <Badge variant="outline" className={STATUS_STYLES[d.status]} data-testid="gov-doc-status">{d.status.replace("_", " ")}</Badge>
              <span className="text-xs font-normal text-muted-foreground">v{d.version}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Owner: {d.owner}</span><span>Effective: {d.effective_date || "—"}</span><span>Review by: {d.review_date || "—"}</span>
            {d.legal_review_required && <span className="text-amber-700 font-medium">Legal review required</span>}
            {d.content_sha256 && <span className={`inline-flex items-center gap-1 ${d.integrity_ok ? "text-emerald-700" : "text-red-700 font-semibold"}`} data-testid="gov-doc-integrity">
              {d.integrity_ok ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}{d.integrity_ok ? "Content matches approved fingerprint" : "CONTENT CHANGED SINCE APPROVAL"}</span>}
          </div>

          {edit ? (
            <div className="space-y-3">
              <div className="grid md:grid-cols-3 gap-2">
                <div><Label className="text-xs">Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="gov-doc-title-input" /></div>
                <div><Label className="text-xs">Effective date</Label><Input type="date" value={edit.effective_date} onChange={(e) => setEdit({ ...edit, effective_date: e.target.value })} data-testid="gov-doc-effective-input" /></div>
                <div><Label className="text-xs">Review date</Label><Input type="date" value={edit.review_date} onChange={(e) => setEdit({ ...edit, review_date: e.target.value })} data-testid="gov-doc-review-input" /></div>
              </div>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={edit.customer_acceptance_required} onCheckedChange={(v) => setEdit({ ...edit, customer_acceptance_required: !!v })} data-testid="gov-doc-acceptance-required" /> Customer must accept this document</label>
              <Textarea rows={22} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} className="font-mono text-xs" data-testid="gov-doc-content-input" />
              <div className="flex gap-2">
                <Button disabled={busy} onClick={() => call(() => api.put(`/governance/documents/${d.id}`, edit), "Draft saved")} data-testid="gov-doc-save-btn">Save draft</Button>
                <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-border bg-white p-4 max-h-[45vh] overflow-y-auto" data-testid="gov-doc-content"><GovMarkdown>{d.content}</GovMarkdown></div>
          )}

          {!edit && (
            <div className="space-y-2 border-t pt-3">
              <div className="flex flex-wrap gap-2">
                {!locked && <Button size="sm" variant="outline" onClick={() => setEdit({ title: d.title, content: d.content, effective_date: d.effective_date || "", review_date: d.review_date || "", customer_acceptance_required: !!d.customer_acceptance_required })} data-testid="gov-doc-edit-btn">Edit draft</Button>}
                {locked && d.status !== "superseded" && <Button size="sm" variant="outline" disabled={busy} onClick={async () => { const nd = await call(() => api.post(`/governance/documents/${d.id}/new-version`), "New draft version created"); if (nd) load(nd.id); }} data-testid="gov-doc-new-version-btn">Create new version</Button>}
                <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadFile(`/governance/documents/${d.id}/pdf`, "document.pdf")} data-testid="gov-doc-pdf-btn"><Download size={13} /> PDF</Button>
              </div>
              {(NEXT[d.status] || []).length > 0 && (
                <div className="flex flex-wrap items-end gap-2">
                  <Input placeholder="Note for the record (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs max-w-xs" data-testid="gov-doc-note-input" />
                  {d.status === "in_review" && d.legal_review_required && (
                    <label className="flex items-center gap-1.5 text-xs text-amber-800"><Checkbox checked={legalOk} onCheckedChange={(v) => setLegalOk(!!v)} data-testid="gov-doc-legal-confirm" /> Legal review completed</label>
                  )}
                  {NEXT[d.status].map((s) => (
                    <Button key={s} size="sm" disabled={busy} variant={s === "withdrawn" ? "outline" : "default"} className={s === "withdrawn" ? "text-red-600" : ""}
                      onClick={() => (s !== "withdrawn" || window.confirm("Withdraw this document?")) && call(() => api.post(`/governance/documents/${d.id}/transition`, { status: s, note, legal_review_confirmed: legalOk }), `Marked ${s.replace("_", " ")}`)}
                      data-testid={`gov-doc-transition-${s}`}>{LABEL[s]}</Button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 text-xs">
            <div>
              <p className="font-semibold mb-1">Versions</p>
              <ul className="space-y-0.5" data-testid="gov-doc-versions">{(d.versions || []).map((v) => (
                <li key={v.id}><button type="button" onClick={() => load(v.id)} className={`hover:underline ${v.id === d.id ? "font-semibold" : ""}`}>v{v.version} · {v.status} · {fmtDate(v.updated_at)}</button></li>
              ))}</ul>
            </div>
            <div>
              <p className="font-semibold mb-1">History</p>
              <ul className="space-y-0.5 text-muted-foreground" data-testid="gov-doc-history">{[...(d.history || [])].reverse().map((h, i) => <li key={i}>{fmtDate(h.at)} · {h.action} · {h.by}{h.note ? ` · ${h.note}` : ""}</li>)}</ul>
            </div>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};
